import { cloudCall } from "./api";

export type AuthUser = {
  id: string;
  email: string;
  name: string;
  token: string;
};

type Kv = {
  get(key: string): Promise<string | null>;
  set(key: string, value: string): Promise<void>;
  del(key: string): Promise<void>;
};

export type MailSender = (input: {
  to: string;
  subject: string;
  html: string;
  text: string;
}) => Promise<{ sent: boolean; reason?: string }>;

type RemoteSession = {
  token: string;
  user: { id: string; email: string; name?: string };
};

/** Fallo de red (sin respuesta del servidor), no un error explicado por el servidor. */
export function isNetworkError(message: string): boolean {
  return /Failed to fetch|Network request failed|NetworkError|Load failed|timeout|abort/i.test(message);
}

/** Claves locales del usuario que ha iniciado sesión en este dispositivo. */
export const LOCAL_SESSION_KEYS = ["hermes.session", "hermes.kit", "hermes.plans"] as const;

/**
 * Cierra la sesión en este dispositivo y borra las copias sin dueño del perfil y los planes,
 * para que otra cuenta que entre después no herede (ni suba a su cuenta) los datos de la anterior.
 */
export async function clearLocalSession(kv: Kv): Promise<void> {
  try {
    const raw = await kv.get("hermes.session");
    const token = raw ? String((JSON.parse(raw) as Partial<AuthUser>).token || "") : "";
    // El token también deja de valer en el servidor (si alguien lo copió, ya no sirve). Sin red, se borra igual.
    if (token) void cloudCall("/auth/logout", { method: "POST", token }).catch(() => undefined);
  } catch {
    /* sesión ilegible: se borra igual */
  }
  for (const key of LOCAL_SESSION_KEYS) {
    try {
      await kv.del(key);
    } catch {
      /* ignore */
    }
  }
}

function hasKitData(kit: { hobbies?: unknown; gear?: unknown } | null | undefined): boolean {
  return Boolean(kit && ((Array.isArray(kit.hobbies) && kit.hobbies.length) || (Array.isArray(kit.gear) && kit.gear.length)));
}

/**
 * Guarda la sesión devuelta por /auth/login, /auth/verify, /auth/google o /auth/reset (mismo formato)
 * y trae perfil y planes de la nube. Si la cuenta aún no tiene perfil en la nube, sube SOLO el perfil
 * guardado localmente para ESA misma cuenta (nunca el de otra persona que usó el móvil antes).
 */
export async function completeLogin(kv: Kv, remote: RemoteSession): Promise<AuthUser> {
  if (!remote || !remote.token || !remote.user || !remote.user.id) {
    throw new Error("El servidor no ha devuelto la sesión. Inténtalo de nuevo.");
  }
  const email = String(remote.user.email || "");
  const user: AuthUser = {
    id: String(remote.user.id),
    email,
    name: String(remote.user.name || "") || email.split("@")[0] || "Hermes",
    token: String(remote.token),
  };
  // Si antes había otra cuenta en este dispositivo, sus copias locales sin dueño no deben mezclarse.
  try {
    const prevRaw = await kv.get("hermes.session");
    const prev = prevRaw ? (JSON.parse(prevRaw) as Partial<AuthUser>) : null;
    if (prev?.id && prev.id !== user.id) {
      await kv.del("hermes.kit");
      await kv.del("hermes.plans");
    }
  } catch {
    /* sesión anterior ilegible: se sobrescribe */
  }
  await kv.set("hermes.session", JSON.stringify(user));
  await kv.del(`hermes.user.${email.toLowerCase()}`);
  try {
    const { pullKit, pullPlans, pushKit } = await import("./sync");
    const kit = await pullKit(user.token);
    if (kit) {
      await kv.set(`hermes.kit.${user.id}`, JSON.stringify(kit));
      await kv.set("hermes.kit", JSON.stringify(kit));
    } else {
      const localRaw = await kv.get(`hermes.kit.${user.id}`);
      if (localRaw) {
        try {
          const local = JSON.parse(localRaw) as { hobbies?: string[]; gear?: string[] };
          if (hasKitData(local)) {
            const clean = { hobbies: Array.isArray(local.hobbies) ? local.hobbies : [], gear: Array.isArray(local.gear) ? local.gear : [] };
            await pushKit(user.token, clean);
            await kv.set("hermes.kit", JSON.stringify(clean));
          }
        } catch {
          /* ignore */
        }
      }
    }
    const plans = await pullPlans(user.token);
    if (plans) {
      await kv.set(`hermes.plans.${user.id}`, JSON.stringify(plans));
      await kv.set("hermes.plans", JSON.stringify(plans));
    }
  } catch {
    /* sin conexión: se sincroniza al abrir Perfil o Planes */
  }
  return user;
}

export function hermesMail(code: string, name: string) {
  const greet = name ? `Hola ${name}` : "Hola";
  return {
    subject: "Hermes · código para salir a la calle",
    text: `${greet}, tu código de Hermes es ${code}. Caduca en 20 minutos.`,
    html: `<div style="font-family:ui-sans-serif,system-ui;background:#1b2430;color:#f6efe6;padding:24px">
      <p style="color:#e07a3d;letter-spacing:2px;font-size:12px">HERMES</p>
      <h1>Un código, y el mapa se enciende</h1>
      <p>${greet}, confirma tu cuenta para que podamos recomendarte bares, parques y rutas con nombre propio.</p>
      <p style="font-size:28px;letter-spacing:8px">${code}</p>
    </div>`,
  };
}

export async function registerAccount(
  kv: Kv,
  _mail: MailSender,
  input: { email: string; password: string; name: string },
): Promise<{ mode: "verify"; mailSent?: boolean; reason?: string; code?: string }> {
  try {
    const remote = await cloudCall<{
      ok?: boolean;
      mailSent?: boolean;
      needsVerification?: boolean;
      code?: string | null;
    }>("/auth/register", {
      method: "POST",
      body: JSON.stringify(input),
    });
    if (remote.ok === true || remote.mailSent === true || remote.needsVerification === true) {
      await kv.set(
        `hermes.pending.${input.email.toLowerCase()}`,
        JSON.stringify({ email: input.email, name: input.name, at: Date.now() }),
      );
      if (remote.mailSent === true) return { mode: "verify", mailSent: true };
      try {
        const again = await cloudCall<{ mailSent?: boolean }>("/auth/resend", {
          method: "POST",
          body: JSON.stringify({ email: input.email }),
        });
        if (again.mailSent === true) return { mode: "verify", mailSent: true };
      } catch {
        /* retry below only if there is no server account */
      }
      return { mode: "verify", mailSent: false };
    }
  } catch (err) {
    const msg = err instanceof Error ? err.message : "";
    if (/Ya existe|Datos inválidos/i.test(msg)) throw err;
    if (msg && !isNetworkError(msg)) {
      throw err;
    }
  }
  throw new Error("Necesitas conexión a internet para crear la cuenta. Inténtalo de nuevo.");
}

export async function verifyAccount(kv: Kv, input: { email: string; code: string }): Promise<AuthUser> {
  try {
    const remote = await cloudCall<RemoteSession>("/auth/verify", {
      method: "POST",
      body: JSON.stringify(input),
    });
    const user = await completeLogin(kv, remote);
    await kv.del(`hermes.pending.${input.email.toLowerCase()}`);
    return user;
  } catch (err) {
    const msg = err instanceof Error ? err.message : "";
    if (msg && !isNetworkError(msg)) throw err;
    throw new Error("Sin conexión: no se pudo comprobar el código. Inténtalo de nuevo.");
  }
}

export async function loginAccount(kv: Kv, input: { email: string; password: string }): Promise<AuthUser> {
  try {
    const remote = await cloudCall<RemoteSession>("/auth/login", {
      method: "POST",
      body: JSON.stringify(input),
    });
    return await completeLogin(kv, remote);
  } catch (err) {
    const msg = err instanceof Error ? err.message : "";
    if (msg && !isNetworkError(msg)) throw err;
  }
  // Sin conexión: seguir con la sesión ya abierta en este dispositivo (si es la misma cuenta)
  const saved = await kv.get("hermes.session");
  if (saved) {
    try {
      const sess = JSON.parse(saved) as AuthUser;
      if (sess.email && sess.email.toLowerCase() === input.email.trim().toLowerCase()) return sess;
    } catch {
      /* ignore */
    }
  }
  throw new Error("No hay conexión con el servidor. Revisa tu internet e inténtalo de nuevo.");
}

/**
 * Entra (o crea la cuenta, ya verificada) con el id_token de Google.
 * Sirve para Android (selector de cuentas nativo) y para la web (Google Identity Services).
 */
export async function loginWithGoogle(kv: Kv, idToken: string): Promise<AuthUser> {
  const token = String(idToken || "").trim();
  if (!token) throw new Error("Google no ha devuelto tus datos de acceso. Inténtalo de nuevo.");
  let remote: RemoteSession;
  try {
    remote = await cloudCall<RemoteSession>("/auth/google", {
      method: "POST",
      body: JSON.stringify({ id_token: token, credential: token }),
    });
  } catch (err) {
    const msg = err instanceof Error ? err.message : "";
    if (!msg || isNetworkError(msg)) {
      throw new Error("No hay conexión con el servidor. Revisa tu internet e inténtalo de nuevo.");
    }
    throw err;
  }
  return completeLogin(kv, remote);
}

export async function resendHermesCode(_kv: Kv, _mail: MailSender, email: string) {
  try {
    const remote = await cloudCall<{ mailSent?: boolean }>("/auth/resend", {
      method: "POST",
      body: JSON.stringify({ email }),
    });
    if (remote.mailSent === true) return;
  } catch (err) {
    const msg = err instanceof Error ? err.message : "";
    if (msg && !isNetworkError(msg)) throw err;
  }
  throw new Error("No se pudo reenviar el código. Revisa tu conexión e inténtalo de nuevo.");
}

/** Pide un código para cambiar la contraseña (el servidor responde igual exista o no la cuenta). */
export async function requestPasswordReset(email: string): Promise<void> {
  await cloudCall<{ ok?: boolean }>("/auth/forgot", {
    method: "POST",
    body: JSON.stringify({ email: email.trim().toLowerCase() }),
  });
}

/** Cambia la contraseña con el código recibido y deja la sesión iniciada. */
export async function resetPassword(
  kv: Kv,
  input: { email: string; code: string; password: string },
): Promise<AuthUser> {
  const remote = await cloudCall<RemoteSession>("/auth/reset", {
    method: "POST",
    body: JSON.stringify({ ...input, email: input.email.trim().toLowerCase() }),
  });
  return completeLogin(kv, remote);
}

/** Elimina la cuenta y todos sus datos del servidor. */
export async function deleteAccount(token: string): Promise<void> {
  await cloudCall<{ ok?: boolean }>("/auth/delete-account", {
    method: "POST",
    token,
    body: JSON.stringify({ confirm: "BORRAR" }),
  });
}
